import {
  Component, Input, Output, EventEmitter,
  OnInit, OnDestroy, ChangeDetectorRef, HostListener,
  ElementRef, Renderer2
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { routeAlias } from '../../../../routesalias';
import { AuthService } from '../../../services/auth/auth.service';
import { closeLocalStorage } from '../../../utils/helpers';
import { NgIf, NgFor, NgClass } from '@angular/common';
import { ClickOutsideDirective } from '../../../directives/click-outside.directive';
import { FormsModule } from '@angular/forms';

export interface MenuItemProps {
  label: string;
  link?: string;
  icon?: string;
  roles?: string[];
  action?: 'logout' | 'link';
  badge?: string;
  children?: MenuItemProps[];
  _open?: boolean;
}

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [RouterLink, RouterLinkActive, MatIconModule, NgIf, NgFor, NgClass, ClickOutsideDirective, FormsModule],
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.css'],
})
export class SidebarComponent implements OnInit, OnDestroy {

  readonly routeAlias = routeAlias;

  /** PC : sidebar repliée en icônes */
  @Input() collapsed = false;
  /** Mobile : désactive le clic-outside pour fermer */
  @Input() disableClickOutside = false;

  /** Mobile : drawer ouvert */
  mobileOpen = false;

  /** Footer dropdown ouvert */
  footerDropdownOpen = false;

  @Output() oneMenuIsClicked = new EventEmitter<void>();
  @Output() clickOutside = new EventEmitter<void>();
  /** Permet au parent de piloter collapse depuis l'extérieur */
  @Output() collapsedChange = new EventEmitter<boolean>();

  menuItems: MenuItemProps[] = [
    { label: 'Tableau de bord', link: routeAlias.home, icon: 'dashboard' },
    {
      label: 'Planification', icon: 'track_changes',
      children: [
        { label: 'Tableau de bord', /* link: routeAlias.activities.dashboard, */ icon: 'dashboard' },
        { label: 'PTA', link: routeAlias.activities.home, icon: 'timeline', roles: ['RA'], },
        { label: 'PTAB', link: routeAlias.budgets.home, icon: 'account_balance_wallet', roles: ['PRMP'] },
        { label: 'PPM', link: routeAlias.marchees.home, icon: 'sell', roles: ['PRMP', 'RA'] },
      ],
    },
    {
      label: 'Execution', icon: 'folder_special', badge: 'En cours', roles: ['ADMIN','SU'],
      children: [
        { label: 'Tableau de bord', /* link: routeAlias.codire.dashboard, */ icon: 'dashboard' },
        {
          label: 'Rapports', icon: 'description',
          children: [
            { label: 'Codire', /* link: routeAlias.codire.raports.moensuels, */ icon: 'calendar_month' },
            { label: 'Rapports mensuels', /* link: routeAlias.codire.raports.moensuels, */ icon: 'calendar_month' },
            { label: 'Rapports Trimestriels', /* link: routeAlias.codire.raports.moensuels, */ icon: 'calendar_month' },
            { label: 'Rapports annuels', /* link: routeAlias.codire.raports.annuels, */ icon: 'bar_chart' },
          ],
        },
        { label: 'Décisions', /* link: routeAlias.codire.decisions, */ icon: 'gavel' },
        { label: 'Membres', /* link: routeAlias.codire.membres, */ icon: 'people' },
      ],
    },
    {
      label: 'Suivi & Evaluation', icon: 'folder_special', badge: 'En cours', roles: ['ADMIN','SU'],
      children: [ ],
    }
  ];

  footerMenuItems: MenuItemProps[] = [
    { label: 'Paramètres', link: routeAlias.parameters.home, icon: 'settings', roles: ['SU', 'PRMP', 'COMPTABLE'], action: 'link' },
    { label: 'Administration', link: routeAlias.administration.home, icon: 'admin_panel_settings', roles: ['ADMIN'], action: 'link' },
  ];

  user: { name?: string; role?: string } | null = null;
  @Input() backgroundColor?: string;

  private isMobile = false;
  private overlayEl: HTMLElement | null = null;
  private overlayClickUnsub: (() => void) | null = null;

  constructor(
    private authService: AuthService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private el: ElementRef,
    private renderer: Renderer2
  ) { }

  ngOnInit(): void {
    this.checkViewport();
    try {
      const info = this.authService.getUserInfo();
      if (info) this.user = {
        name: info.username || info.preferred_username || '',
        role: info.roles?.[0] || ''
      };
    } catch { }
    this.autoOpenActiveGroup(this.menuItems);
  }

  ngOnDestroy(): void { this.destroyOverlay(); }

  // ── Overlay injecté dans <body> pour éviter les problèmes de stacking context ──
  private createOverlay(): void {
    if (this.overlayEl) return;
    this.overlayEl = this.renderer.createElement('div');
    const styles: Record<string, string> = {
      position: 'fixed', inset: '0',
      background: 'rgba(15,23,42,.45)',
      zIndex: '199',
      opacity: '0',
      transition: 'opacity 0.22s ease',
      backdropFilter: 'blur(2px)',
    };
    Object.entries(styles).forEach(([k, v]) => this.renderer.setStyle(this.overlayEl, k, v));
    this.renderer.appendChild(document.body, this.overlayEl);
    // Laisse le temps au DOM de rendre, puis anime
    requestAnimationFrame(() => this.renderer.setStyle(this.overlayEl, 'opacity', '1'));
    this.overlayClickUnsub = this.renderer.listen(this.overlayEl, 'click', () => this.closeMobile());
  }

  private destroyOverlay(): void {
    if (!this.overlayEl) return;
    this.renderer.setStyle(this.overlayEl, 'opacity', '0');
    setTimeout(() => {
      if (this.overlayEl) { this.renderer.removeChild(document.body, this.overlayEl); this.overlayEl = null; }
      this.overlayClickUnsub?.();
    }, 230);
  }

  // ── Responsive ──────────────────────────────────
  @HostListener('window:resize')
  onResize(): void { this.checkViewport(); }

  private checkViewport(): void {
    this.isMobile = window.innerWidth <= 768;
    // Sur desktop, fermer le drawer mobile si on redimensionne
    if (!this.isMobile && this.mobileOpen) { this.mobileOpen = false; }
    this.cdr.markForCheck();
  }

  /** Appelé depuis le parent (ex: bouton hamburger dans la topbar) */
  openMobile(): void {
    this.mobileOpen = true;
    this.renderer.addClass(this.el.nativeElement, 'mobile-open');
    this.createOverlay();
    this.cdr.markForCheck();
  }

  closeMobile(): void {
    this.mobileOpen = false;
    this.renderer.removeClass(this.el.nativeElement, 'mobile-open');
    this.destroyOverlay();
    this.cdr.markForCheck();
  }

  toggleMobile(): void { this.mobileOpen ? this.closeMobile() : this.openMobile(); }

  /** Collapse PC (piloté aussi depuis le bouton interne) */
  toggleCollapse(): void {
    this.collapsed = !this.collapsed;
    // Ferme tous les groupes ouverts quand on replie
    if (this.collapsed) { this.closeAllGroups(this.menuItems); this.footerDropdownOpen = false; }
    this.collapsedChange.emit(this.collapsed);
    this.cdr.markForCheck();
  }

  private closeAllGroups(items: MenuItemProps[]): void {
    for (const item of items) {
      item._open = false;
      if (item.children) this.closeAllGroups(item.children);
    }
  }

  // ── Groupes ─────────────────────────────────────
  toggleGroup(item: MenuItemProps, event: Event): void {
    event.stopPropagation();
    event.preventDefault();
    item._open = !item._open;
    this.cdr.markForCheck();
  }

  hasChildren(item: MenuItemProps): boolean { return !!(item.children?.length); }

  private autoOpenActiveGroup(items: MenuItemProps[]): boolean {
    let found = false;
    for (const item of items) {
      if (item.children) {
        if (this.autoOpenActiveGroup(item.children)) { item._open = true; found = true; }
      } else if (item.link && this.isLinkActive(item.link)) { found = true; }
    }
    return found;
  }

  isLinkActive(link?: string): boolean {
    if (!link) return false;
    try {
      const url = this.router.url || '';
      return url === link || url.startsWith(link + '/');
    } catch { return false; }
  }

  isActive(item: MenuItemProps): boolean { return this.isLinkActive(item.link); }

  hasActiveChild(item: MenuItemProps): boolean {
    if (!item.children) return false;
    return item.children.some(c => this.isLinkActive(c.link) || this.hasActiveChild(c));
  }

  onClickMenu(): void {
    try { closeLocalStorage(); } catch { }
    this.oneMenuIsClicked.emit();
    if (this.isMobile) this.closeMobile();
  }

  handleClickOutside(): void {
    if (!this.disableClickOutside) {
      this.clickOutside.emit();
      if (this.isMobile) this.closeMobile();
    }
  }

  // ── Footer dropdown ──────────────────────────────
  toggleFooterDropdown(event: Event): void {
    event.stopPropagation();
    this.footerDropdownOpen = !this.footerDropdownOpen;
    this.cdr.markForCheck();
  }

  closeFooterDropdown(): void {
    if (!this.footerDropdownOpen) return;
    this.footerDropdownOpen = false;
    this.cdr.markForCheck();
  }

  logout(): void { try { this.authService.logout(); } catch { } }
  hasAccess(roles?: string[]): boolean {
    return !roles?.length || this.authService.hasAnyRole(roles);
  }
}